import React from "react";
import { Check, ChevronRight } from "lucide-react";

/**
 * Stepper visuel ultra-moderne 2026 — cartes rondies avec numéros + sub-label.
 * Inspiré du mockup demo.PNG fourni par l'utilisateur.
 *
 * États par étape :
 *  - "done"    → cercle bleu rempli avec ✓ + label normal
 *  - "current" → cercle bleu avec numéro blanc, carte avec bordure bleue + glow
 *  - "todo"    → cercle bordé gris avec numéro muté + label muté
 */
export default function EventStepper({ steps, activeStep, onStepClick, title = "Progression de l'événement", headerAction = null }) {
  // Pourcentage de progression visuelle
  const progressPct = Math.max(0, Math.min(100, ((activeStep - 1) / (steps.length - 1)) * 100));

  return (
    <div
      className="card-elevated mb-8 overflow-hidden"
      data-testid="event-stepper"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="label">{title}</div>
        {headerAction}
      </div>

      {/* Steps row */}
      <div className="relative">
        <div className="flex items-stretch gap-2 overflow-x-auto pb-2 -mx-1 px-1">
          {steps.map((s, idx) => {
            const stepNum = idx + 1;
            const isDone = stepNum < activeStep;
            const isCurrent = stepNum === activeStep;
            const status = isDone ? "done" : isCurrent ? "current" : "todo";
            const isLast = idx === steps.length - 1;
            const clickable = !!onStepClick;

            return (
              <React.Fragment key={s.id}>
                <button
                  type="button"
                  onClick={clickable ? () => onStepClick(s, stepNum) : undefined}
                  disabled={!clickable}
                  title={s.tooltip || s.label}
                  data-testid={`stepper-step-${s.id}`}
                  data-status={status}
                  className={`group flex items-center gap-3 px-4 py-3 rounded-xl transition-all min-w-[180px] text-left border ${
                    isCurrent
                      ? "border-[var(--text)] bg-[var(--surface)] shadow-[0_0_0_3px_var(--accent-ring)]"
                      : isDone
                      ? "border-[var(--border-strong)] bg-[var(--surface)] hover:bg-[var(--surface-3)]"
                      : "border-[var(--border)] bg-transparent"
                  } ${clickable ? "cursor-pointer" : "cursor-default"}`}
                >
                  {/* Number / Check circle */}
                  <span
                    className={`shrink-0 w-8 h-8 flex items-center justify-center rounded-full font-semibold text-xs tabular transition-colors ${
                      isCurrent
                        ? "bg-[var(--text)] text-[var(--surface)]"
                        : isDone
                        ? "bg-[var(--text)] text-[var(--surface)]"
                        : "border border-[var(--muted-2)] text-[var(--muted)]"
                    }`}
                  >
                    {isDone ? <Check size={14} strokeWidth={3} /> : stepNum}
                  </span>

                  {/* Label */}
                  <span className="flex flex-col leading-tight">
                    <span
                      className={`text-sm font-semibold tracking-tight ${
                        isCurrent
                          ? "text-[var(--text)]"
                          : isDone
                          ? "text-[var(--text)]"
                          : "text-[var(--muted)]"
                      }`}
                    >
                      {s.label}
                    </span>
                    {s.subLabel && (
                      <span
                        className={`text-[11px] mt-0.5 font-normal ${
                          isCurrent ? "text-[var(--text-2)]" : "text-[var(--muted)]"
                        }`}
                      >
                        {s.subLabel}
                      </span>
                    )}
                  </span>
                </button>

                {!isLast && (
                  <span className="self-center text-[var(--muted-2)] shrink-0">
                    <ChevronRight size={16} strokeWidth={2} />
                  </span>
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Progress bar */}
        <div className="progress-track mt-3">
          <div className="progress-fill" style={{ width: `${progressPct}%` }} />
        </div>
      </div>
    </div>
  );
}
